/**
 * Conversion between PowerSync `block` rows and the TipTap document.
 *
 * The complete document is never persisted: each top-level node is
 * serialized as an independent row, sorted by `sort_key`.
 */
import type { JSONContent } from "@tiptap/core";
import type { Block } from "@folio/powersync";
import { type EditorBlockNode, isSidecarBlockType } from "@folio/shared";

const EMPTY_PARAGRAPH: JSONContent = { type: "paragraph", attrs: { blockId: null } };

function isJsonNode(value: unknown): value is JSONContent {
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    typeof (value as { type: unknown }).type === "string"
  );
}

/**
 * Builds the TipTap document from the local rows.
 * The rows arrive already sorted by sort_key (getPageBlocksQuery).
 */
export function blocksToDoc(blocks: Block[]): JSONContent {
  const content: JSONContent[] = [];
  for (const row of blocks) {
    // Sidecar rows (comment threads) live on the page but are NOT editor
    // content: skip them silently (they render in the comments panel).
    if (!row.content || isSidecarBlockType(row.type)) {
      continue;
    }
    try {
      const parsed: unknown = JSON.parse(row.content);
      if (!isJsonNode(parsed)) {
        console.warn(`[notes] Skipping malformed block ${row.id}`);
        continue;
      }
      parsed.attrs = { ...parsed.attrs, blockId: row.id };
      content.push(parsed);
    } catch {
      // Corrupt row: ignore it instead of breaking the whole page
      console.warn(`[notes] Skipping malformed block ${row.id}`);
    }
  }
  if (content.length === 0) {
    content.push(EMPTY_PARAGRAPH);
  }
  return { type: "doc", content };
}

/**
 * Plain text of a TipTap node (`text` leaves + wiki-link labels).
 * It is persisted in `block.text` for LIKE searches without JSON noise.
 */
export function extractNodeText(node: JSONContent): string {
  if (typeof node.text === "string") {
    return node.text;
  }
  if (node.type === "wikiLink") {
    const label: unknown = node.attrs?.label;
    return typeof label === "string" ? label : "";
  }
  return (node.content ?? [])
    .map((child) => extractNodeText(child))
    .filter((part) => part.length > 0)
    .join(" ");
}

/**
 * Extracts the document's top-level nodes as blocks ready
 * for `syncPageBlocksMutation`. Nodes without blockId (which should not happen after
 * the BlockId extension) are omitted: the next pass will persist them.
 */
export function docToBlockNodes(doc: JSONContent): EditorBlockNode[] {
  const nodes: EditorBlockNode[] = [];
  for (const node of doc.content ?? []) {
    const id: unknown = node.attrs?.blockId;
    if (typeof id !== "string" || id.length === 0 || !node.type) {
      continue;
    }
    nodes.push({
      id,
      type: node.type,
      content: JSON.stringify(node),
      text: extractNodeText(node),
    });
  }
  return nodes;
}

/**
 * Stable fingerprint of the content of a set of blocks, to detect whether
 * a remote change differs from what the editor already displays.
 */
export function blockNodesFingerprint(nodes: EditorBlockNode[]): string {
  return nodes.map((node) => `${node.id}:${node.content}`).join(" ");
}
